import { listOptionalFieldsForWorkflow } from '@100mslive/disposition-engine';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type DispositionHelperResult, useDispositionHelper } from '@/hooks/useDispositionHelper';
import { resolveEngineWorkflowType } from '@/lib/disposition/engineWorkflowType';

/** A disposition's field lists, as carried on `call.disposition` / `case.disposition`. */
export interface DispositionFields {
  required_fields?: string[];
  missing_fields?: string[];
}

export interface ResolvedDisposition {
  /** Required field paths: the engine output when supported, else the fallback. */
  requiredFields: string[];
  /** Missing field paths: the engine output when supported, else the fallback. */
  missingFields: string[];
  /** Convenience `{ required_fields, missing_fields }` for `FieldGroupAccordion`. */
  dispositionFields: { required_fields: string[]; missing_fields: string[] };
}

/**
 * Pure resolution of the disposition field lists: prefer the live engine result
 * when the workflow type is supported, otherwise fall back to a persisted
 * disposition (call- or case-level — the caller's choice).
 *
 * Extracted from the hook so it can be unit-tested without rendering (the
 * dashboard intentionally has no `@testing-library/react` / renderHook infra —
 * see `TableBuilder.test.tsx`).
 *
 * `optionalFields` (per workflow type, via `listOptionalFieldsForWorkflow`) are the
 * always-collected fields the engine strips from `required_params` because they never
 * block a disposition. When supplied they are merged into `requiredFields` so the
 * human call script surfaces them — but NOT into `missingFields`, so an empty one
 * shows as a required field without counting as pending or blocking step advancement.
 */
export function resolveDispositionFields(
  helper: Pick<DispositionHelperResult, 'supported' | 'current' | 'missingFields'>,
  fallback: DispositionFields | null | undefined,
  optionalFields: string[] = [],
): ResolvedDisposition {
  const useEngine = helper.supported && !!helper.current;
  // Pick required/missing from the engine result or the persisted fallback in one
  // branch (keeps cyclomatic complexity within lint bounds).
  const source = useEngine
    ? { required: helper.current?.required_params ?? [], missing: helper.missingFields }
    : { required: fallback?.required_fields ?? [], missing: fallback?.missing_fields ?? [] };
  // Merge always-collected fields back in (deduped, base order preserved). Left out
  // of `missingFields` on purpose — see the JSDoc above: surfaced, not blocking.
  const requiredFields = mergeOptionalFields(source.required, optionalFields);
  return {
    requiredFields,
    missingFields: source.missing,
    dispositionFields: { required_fields: requiredFields, missing_fields: source.missing },
  };
}

/** Append `optionalFields` to `base`, deduped, preserving base order. No-op when empty. */
function mergeOptionalFields(base: string[], optionalFields: string[]): string[] {
  return optionalFields.length ? [...new Set([...base, ...optionalFields])] : base;
}

export interface UseCommittedDispositionParams {
  /** Workflow type that drives the engine; `undefined` disables it (unsupported). */
  workflowType: string | undefined;
  /** Persisted/server baseline output. Re-syncs the snapshot when its ref changes. */
  baselineOutput: Record<string, unknown> | null | undefined;
  /** In-flight edited/working output, snapshotted on commit. */
  editedOutput: Record<string, unknown> | null | undefined;
  /** When true the snapshot tracks `editedOutput`; when false it tracks `baselineOutput`. */
  isEditing: boolean;
  /**
   * Case input (seed) data. Merged UNDER the committed output before the engine
   * runs — `{ ...inputData, ...committedOutput }` — so disposition rules that
   * reference case inputs resolve. Output wins on key collisions; input fills gaps.
   * Not commit-gated: input is not edited during a call, so its ref is stable.
   */
  inputData?: Record<string, unknown> | null;
  /** Disposition to use when the engine doesn't support `workflowType`. */
  fallbackDisposition?: DispositionFields | null;
  /**
   * The case workflow's agent UUID (`useEngineAgentId`). The engine resolves program
   * flags — and therefore every program-flag-gated required field — solely from this
   * value; omitting it makes all flags resolve `false` (BI-1795).
   */
  agentId?: string;
  /**
   * When true, the workflow's always-collected "optional" fields
   * (`listOptionalFieldsForWorkflow`) are merged into `requiredFields` so they
   * surface in the human call script. Off by default — the Review tab, which
   * shares this hook, keeps the engine's required set unchanged.
   */
  alwaysIncludeOptionalFields?: boolean;
}

export interface UseCommittedDispositionResult extends ResolvedDisposition {
  /** The committed snapshot fed to the engine (exposed for debug/inspection). */
  committedOutput: Record<string, unknown> | null;
  /** Call on field commit (blur for text; change for instant-commit controls). */
  handleOutputCommit: () => void;
  /** Raw engine result, for callers that pass it straight to a panel. */
  helper: DispositionHelperResult;
  supported: boolean;
  current: DispositionHelperResult['current'];
}

/**
 * Shared committed-output snapshot + disposition engine wiring.
 *
 * The disposition engine must re-evaluate ONLY on field COMMITs, never on
 * in-flight keystrokes (the `useDispositionHelper` contract). This hook keeps a
 * `committedOutput` snapshot that advances on commit (`handleOutputCommit`) and
 * when the server baseline shifts, runs the engine against it, and resolves the
 * required/missing field lists (engine result, or a caller-supplied fallback for
 * unsupported workflow types).
 *
 * Both `DefaultCaseDetailsPage` (Review tab) and the Human Call tab consume this
 * so the snapshot/commit dance lives in exactly one place.
 */
export function useCommittedDisposition({
  workflowType,
  baselineOutput,
  editedOutput,
  isEditing,
  fallbackDisposition,
  alwaysIncludeOptionalFields = false,
  inputData,
  agentId,
}: UseCommittedDispositionParams): UseCommittedDispositionResult {
  // Lazy init to the correct snapshot so disposition is right on first render
  // (no null flash): the effective edited output when editing, else the baseline.
  const [committedOutput, setCommittedOutput] = useState<Record<string, unknown>>(() =>
    isEditing
      ? ((editedOutput ?? baselineOutput ?? {}) as Record<string, unknown>)
      : ((baselineOutput ?? {}) as Record<string, unknown>),
  );

  // Re-sync when the server baseline shifts or edit mode toggles. Deliberately
  // NOT depending on `editedOutput`: keystrokes must not move the snapshot — only
  // commits (commitVersion below) and baseline/edit-mode changes do.
  useEffect(() => {
    if (isEditing) {
      setCommittedOutput((editedOutput ?? baselineOutput ?? {}) as Record<string, unknown>);
    } else {
      setCommittedOutput((baselineOutput ?? {}) as Record<string, unknown>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, baselineOutput]);

  // Freshest effective output, read by the commit effect to avoid a one-render
  // lag for instant-commit controls (select/date/number), where onChange and the
  // commit fire in the same synchronous handler — a value captured at schedule
  // time would lag by a render.
  const effectiveRef = useRef<Record<string, unknown>>({});
  effectiveRef.current = (editedOutput ?? baselineOutput ?? {}) as Record<string, unknown>;

  // Commit-on-blur via a render-version counter. `handleOutputCommit` bumps the
  // version; the effect re-runs after React flushes the render that updated the
  // working output, so it snapshots the latest value from the ref.
  const [commitVersion, setCommitVersion] = useState(0);
  useEffect(() => {
    if (commitVersion === 0) return;
    setCommittedOutput(effectiveRef.current);
  }, [commitVersion]);
  const handleOutputCommit = useCallback(() => setCommitVersion(v => v + 1), []);

  const helper = useDispositionHelper(workflowType, committedOutput, inputData, agentId);

  // Per-workflow always-collected fields (empty unless opted in). Memoized so the
  // `resolved` memo's dependency stays referentially stable across renders.
  // `listOptionalFieldsForWorkflow` is additive — it returns [] for an
  // unsupported/unknown workflow_type and never throws.
  const optionalFields = useMemo(
    () =>
      alwaysIncludeOptionalFields && workflowType
        ? listOptionalFieldsForWorkflow(resolveEngineWorkflowType(workflowType))
        : [],
    [alwaysIncludeOptionalFields, workflowType],
  );

  const resolved = useMemo(
    () => resolveDispositionFields(helper, fallbackDisposition, optionalFields),
    // Depend on the inner values we read, not the freshly-memoized `helper`
    // object or the `fallbackDisposition` ref (often rebuilt each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      helper.supported,
      helper.current,
      helper.missingFields,
      fallbackDisposition?.required_fields,
      fallbackDisposition?.missing_fields,
      optionalFields,
    ],
  );

  return {
    committedOutput,
    handleOutputCommit,
    helper,
    supported: helper.supported,
    current: helper.current,
    ...resolved,
  };
}
